<template>
  <div class="game-distribution">
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>赛事分布</span>
        <div class="operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportType" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenType" />
          </el-tooltip>
          <el-divider direction="vertical" />
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportLevel" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenLevel" />
          </el-tooltip>
        </div>
      </div>
      <div class="chart-container">
        <div class="chart-row">
          <div ref="typeChart" class="sub-chart"></div>
          <div ref="levelChart" class="sub-chart"></div>
        </div>
      </div>
    </el-card>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog vertical-center-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose"
    >
      <div slot="title" class="dialog-header">
        <span>{{ dialogTitle }}</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

export default {
  name: 'GameDistributionChart',
  data() {
    return {
      typeChart: null,
      levelChart: null,
      chartData: {
        types: [],
        levels: []
      },
      fullscreenChart: null,
      dialogVisible: false,
      dialogTitle: '',
      currentChartType: '',
      resizeHandler: null,
      isInitializing: false
    }
  },
  mounted() {
    this.initCharts()
    this.fetchData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    if (this.typeChart) {
      this.typeChart.dispose()
    }
    if (this.levelChart) {
      this.levelChart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeCharts)
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    }
  },
  methods: {
    initCharts() {
      this.typeChart = echarts.init(this.$refs.typeChart)
      this.levelChart = echarts.init(this.$refs.levelChart)
    },
    resizeCharts() {
      this.typeChart?.resize()
      this.levelChart?.resize()
    },
    async fetchData() {
      try {
        const res = await request.get('/api/analysis/game/distribution')
        this.chartData = {
          types: res.data.types || [],
          levels: res.data.levels || []
        }
        this.updateCharts()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      }
    },
    updateCharts() {
      // 动态获取数据，不再使用固定数组
      const typeData = this.chartData.types || []
      const levelData = this.chartData.levels || []
      
      // 计算分组数量，将图例平均分为两组
      const typeDataLen = typeData.length
      const levelDataLen = levelData.length
      const typeRows = Math.ceil(typeDataLen / 2)
      const levelRows = Math.ceil(levelDataLen / 2)
      
      // 赛事类型环形饼图
      const typeOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: (params) => {
            if (!params || !params.data) return ''
            const name = params.name || ''
            const value = params.value || 0
            const percent = params.percent || 0
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${name}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  <div>数量：${value}</div>
                  <div>占比：${percent.toFixed(1)}%</div>
                </div>
              </div>
            `
          }
        },
        title: {
          text: '赛事类型分布',
          left: 'center',
          top: 0,
          textStyle: {
            color: '#2E365A',
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        legend: {
          type: 'plain',
          orient: 'vertical',
          right: '4%',
          top: 'middle',
          itemGap: 10,
          itemWidth: 12,
          itemHeight: 10,
          textStyle: {
            fontSize: 12
          },
          formatter: function(name) {
            if (name.length > 6) {
              return name.substring(0, 6) + '...';
            }
            return name;
          },
          data: typeData.map(item => item.name)
        },
        series: [
          {
            name: '赛事类型分布',
            type: 'pie',
            radius: ['38%', '58%'],
            center: ['32%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 8,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '14',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: typeData
          }
        ]
      }

      // 赛事等级环形饼图
      const levelOption = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: (params) => {
            if (!params || !params.data) return ''
            const name = params.name || ''
            const value = params.value || 0
            const percent = params.percent || 0
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${name}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  <div>数量：${value}</div>
                  <div>占比：${percent.toFixed(1)}%</div>
                </div>
              </div>
            `
          }
        },
        title: {
          text: '赛事等级分布',
          left: 'center',
          top: 0,
          textStyle: {
            color: '#2E365A',
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        legend: {
          type: 'plain',
          orient: 'vertical',
          right: '4%',
          top: 'middle',
          itemGap: 10,
          itemWidth: 12,
          itemHeight: 10,
          textStyle: {
            fontSize: 12
          },
          formatter: function(name) {
            if (name.length > 6) {
              return name.substring(0, 6) + '...';
            }
            return name;
          },
          data: levelData.map(item => item.name)
        },
        series: [
          {
            name: '赛事等级分布',
            type: 'pie',
            radius: ['40%', '60%'],
            center: ['38%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 8,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '14',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: levelData
          }
        ]
      }

      this.typeChart.setOption(typeOption)
      this.levelChart.setOption(levelOption)
    },
    handleExportType() {
      const dataURL = this.typeChart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '赛事类型分布.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleExportLevel() {
      const dataURL = this.levelChart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '赛事等级分布.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      // 确保在对话框完全打开后初始化图表
      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom || !this.currentChartType) return

          // 初始化新的图表实例
          this.fullscreenChart = echarts.init(chartDom)
          let option = null

          // 根据类型获取对应的图表配置
          if (this.currentChartType === 'type') {
            option = this.typeChart.getOption()
          } else {
            option = this.levelChart.getOption()
          }

          if (option) {
            // 调整全屏显示时的系列配置
            if (option.series && option.series[0]) {
              option.series[0].radius = ['30%', '65%']
              option.series[0].center = ['50%', '45%']
            }
            
            // 调整全屏图表的图例位置，保持分两行显示
            if (option.legend && Array.isArray(option.legend)) {
              option.legend[0].bottom = 50
              option.legend[1].bottom = 30
            }
            
            this.fullscreenChart.setOption(option)
            
            // 创建新的 resize 处理函数
            this.resizeHandler = _.debounce(() => {
              if (this.fullscreenChart) {
                this.fullscreenChart.resize()
              }
            }, 100)

            // 添加 resize 事件监听
            window.addEventListener('resize', this.resizeHandler)
          }
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClose() {
      // 在关闭前清理
      this.cleanupFullscreen()
    },
    handleDialogClosed() {
      // 在完全关闭后重置状态
      this.currentChartType = ''
      this.isInitializing = false
    },
    cleanupFullscreen() {
      // 移除 resize 事件监听
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }

      // 销毁图表实例
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    handleFullscreenType() {
      // 先清理旧的实例
      this.cleanupFullscreen()
      this.currentChartType = 'type'
      this.dialogTitle = '赛事类型分布'
      this.dialogVisible = true
    },
    handleFullscreenLevel() {
      this.cleanupFullscreen()
      this.currentChartType = 'level'
      this.dialogTitle = '赛事等级分布'
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dataURL = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })
        
        const link = document.createElement('a')
        link.download = `${this.dialogTitle}.png`
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    }
  }
}
</script>

<style scoped>
.game-distribution {
  width: calc(50% - 10px);
  max-width: calc(50% - 10px);
  box-sizing: border-box;
  flex: 1 1 0;
  min-width: 0;
}

.chart-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  height: 100%;
}

.chart-card :deep(.el-card__header) {
  padding: 0;
  border-bottom: none;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.chart-container {
  width: 100%;
  height: 400px;
  padding: 20px;
  box-sizing: border-box;
}

.chart-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  gap: 24px;
}

.sub-chart {
  flex: 0 0 calc(50% - 12px);
  height: 320px;
  padding: 0;
}

.operations {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operations .el-button {
  color: #ffffff;
}

.operations .el-button:hover {
  color: #BD6C73;
}

.el-button [class^="el-icon-"] {
  font-size: 16px;
}

.el-divider--vertical {
  height: 16px;
  margin: 0 4px;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 600px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style> 